import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PageHelmet from '../components/helmet/PageHelmet';
import { getDocumentList } from '../redux/actions/documentActions';
import { InfoIcon, SearchIcon, EyeIcon, DownloadIcon, FileTextIcon, BookTextIcon, FileIcon, XIcon, RotateCcwIcon } from '../components/icons/Icons';
import styles from '../css/ArticleDocumentationPage.module.css';

const DOCUMENT_BASE_URL = 'https://resource.ewe.rs/media/';

function getDocumentUrl(acDocumentPath) {
    return `${DOCUMENT_BASE_URL}${(acDocumentPath || '').trim()}`;
}

function getDocumentTypeIcon(type, className) {
    if (type === 'Lista sa podacima') {
        return <FileTextIcon className={className} />;
    }
    if (type === 'Uputstvo') {
        return <BookTextIcon className={className} />;
    }
    return <FileIcon className={className} />;
}

function getPageNumbers(current, total) {
    const pages = [];
    const delta = 1;
    const range = [];

    for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i++) {
        range.push(i);
    }

    if (range[0] > 1) {
        pages.push(1);
        if (range[0] > 2) {
            pages.push('...');
        }
    }

    pages.push(...range);

    if (range[range.length - 1] < total) {
        if (range[range.length - 1] < total - 1) {
            pages.push('...');
        }
        pages.push(total);
    }

    return pages;
}

function getDocumentCountForm(count, t) {
    if (count === 1) {
        return t('countSingular');
    }
    if (count >= 2 && count <= 9) {
        return t('countPluralFew');
    }
    return t('countPluralMany');
}

const ArticleDocumentationPage = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation('articleDocumentationPage');

    const [documentList, setDocumentList] = useState([]);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(12);
    const [selectedDocumentType, setSelectedDocumentType] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

    const fetchDocuments = (page, size, documentType, term) => {
        dispatch(getDocumentList(documentType, term, page, size)).then(res => {
            if (res && res.isOk == '1') {
                setDocumentList(res.documentList || []);
                setDocumentTypes(res.documentTypes || []);
                setTotalPages(res.totalPages || 0);
                setTotalCount(res.totalCount || 0);
            }
        });
    };

    useEffect(() => {
        fetchDocuments(0, pageSize, '', '');
    }, [])

    const handleFilterClick = (type, index) => {
        const newType = index === 0 ? '' : type;
        setSelectedDocumentType(newType);
        setCurrentPage(0);
        fetchDocuments(0, pageSize, newType, appliedSearchTerm);
    };

    const goToPage = (page) => {
        if (page < 0 || page >= totalPages || page === currentPage) return;
        setCurrentPage(page);
        fetchDocuments(page, pageSize, selectedDocumentType, appliedSearchTerm);
    };

    const handlePageSizeChange = (size) => {
        setPageSize(size);
        setCurrentPage(0);
        fetchDocuments(0, size, selectedDocumentType, appliedSearchTerm);
    };

    const handleSearchSubmit = () => {
        setAppliedSearchTerm(searchTerm);
        setCurrentPage(0);
        fetchDocuments(0, pageSize, selectedDocumentType, searchTerm);
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearchSubmit();
        }
    };

    const handleClearSearch = () => {
        setSearchTerm('');
    };

    const handleResetAll = () => {
        setSearchTerm('');
        setAppliedSearchTerm('');
        setSelectedDocumentType('');
        setCurrentPage(0);
        setPageSize(12);
        fetchDocuments(0, 12, '', '');
    };

    const isFilterActive = (type, index) => index === 0 ? selectedDocumentType === '' : selectedDocumentType === type;

    const handleOpenDocument = (doc) => {
        window.open(getDocumentUrl(doc.acDocumentPath), '_blank', 'noopener,noreferrer');
    };

    const handleDownloadDocument = async (doc) => {
        const url = getDocumentUrl(doc.acDocumentPath);
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = url.split('/').pop();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error(error);
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <>
            <PageHelmet
                title={t('naslov')}
                description={t('podnaslov')}
                ogDescription={t('podnaslov')}
                ogTitle={t('naslov')}
                ogImg="https://www.ewe.rs/media/ewe-og-img.jpg"
                ogType="website"
                twitterDescription={t('podnaslov')}
            />
            <div className="container-content container-main-content article-documentation-content">
                <div className={`container-fluid ${styles.wideContainer}`}>
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="stats-order-content-holder">
                                <h1>{t('naslov')}</h1>
                                <p>{t('podnaslov')}</p>
                            </div>
                        </div>
                        <div className="col-sm-12">
                            <div className={styles.searchSection}>
                                <div className={styles.searchSectionLeft}>
                                    <label className={styles.searchLabel}>{t('labelPretraga')}</label>
                                    <div className={styles.searchInputGroup}>
                                        <div className={styles.searchInputWrapper}>
                                            <SearchIcon />
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder={t('placeholderPretraga')}
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                onKeyDown={handleSearchKeyDown}
                                            />
                                            {searchTerm && (
                                                <button
                                                    type="button"
                                                    className={styles.clearSearchButton}
                                                    onClick={handleClearSearch}
                                                    aria-label={t('ariaObrisiPretragu')}
                                                >
                                                    <XIcon />
                                                </button>
                                            )}
                                        </div>
                                        <button type="button" className="btn btn-success" onClick={handleSearchSubmit}>{t('dugmePretrazi')}</button>
                                    </div>
                                    <p className={styles.searchHint}>{t('infoPretraga')}</p>
                                </div>
                                <div className={styles.infoBox}>
                                    <div className={styles.infoBoxHeader}>
                                        <InfoIcon />
                                        <span>{t('infoBoxNaslov')}</span>
                                    </div>
                                    <p>{t('infoBoxText1')}</p>
                                    <p>{t('infoBoxText2')}</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-12">
                            <div className={styles.filterBarRow}>
                                <div className={styles.filterButtons}>
                                    {documentTypes.map((type, index) => (
                                        <button
                                            key={type}
                                            type="button"
                                            className={`${styles.filterButton} ${isFilterActive(type, index) ? styles.filterButtonActive : ''}`}
                                            onClick={() => handleFilterClick(type, index)}
                                        >
                                            {type}
                                        </button>
                                    ))}
                                </div>
                                <button type="button" className={styles.resetButton} onClick={handleResetAll}>
                                    <RotateCcwIcon /> {t('dugmeResetuj')}
                                </button>
                            </div>

                            <div className={styles.resultsBar}>
                                <div className={styles.resultsCount}>{t('pronadjenoPrefix')} <span className={styles.resultsCountNumber}>{totalCount}</span> {getDocumentCountForm(totalCount, t)}</div>
                                {/* <div className={styles.sortGroup}>
                                    <label className={styles.sortLabel}>{t('labelSortiranje')}</label>
                                    <select className="form-control form-control-sm" defaultValue="nazivAZ">
                                        <option value="nazivAZ">{t('sortNazivAZ')}</option>
                                        <option value="nazivZA">{t('sortNazivZA')}</option>
                                        <option value="tip">{t('sortTip')}</option>
                                    </select>
                                </div> */}
                            </div>

                            <div className={styles.documentsGrid}>
                                {documentList.map((doc, index) => {
                                    const idents = (doc.acProductList || '').split(',').map(ident => ident.trim()).filter(Boolean);
                                    const visibleIdents = idents.slice(0, 5);
                                    const remaining = Math.max((doc.anProductCount || idents.length) - visibleIdents.length, 0);

                                    return (
                                        <div className={styles.documentCard} key={doc._id ?? index}>
                                            <div className={styles.documentCardHeader}>
                                                {getDocumentTypeIcon(doc.acDocumentType, styles.documentCardIcon)}
                                                <div>
                                                    <div className={styles.documentCardTitle}>{doc.acDocumentName}</div>
                                                    <div className={styles.documentCardMeta}>{t('labelTipDokumenta')} <strong>{doc.acDocumentType}</strong></div>
                                                    <div className={styles.documentCardMeta}>{t('labelPovezanoArtikala')} <strong>{doc.anProductCount}</strong></div>
                                                </div>
                                            </div>
                                            {visibleIdents.length > 0 && (
                                                <div className={styles.documentCardProducts}>
                                                    {visibleIdents.join(', ')}
                                                    {remaining > 0 && (
                                                        <span className={styles.documentCardMore}>{t('plusJos')} {remaining} {t('artikala')}</span>
                                                    )}
                                                </div>
                                            )}
                                            <div className={styles.documentCardPath}>{doc.acDocumentPath && doc.acDocumentPath.trim()}</div>
                                            <div className={styles.documentCardActions}>
                                                <button type="button" className={styles.cardActionButton} onClick={() => handleOpenDocument(doc)}>
                                                    <EyeIcon /> {t('dugmeOtvori')}
                                                </button>
                                                <button type="button" className={`${styles.cardActionButton} ${styles.downloadButton}`} onClick={() => handleDownloadDocument(doc)}>
                                                    <DownloadIcon /> {t('dugmePreuzmi')}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className={styles.paginationBar}>
                                <div className={styles.paginationNumbers}>
                                    <button
                                        type="button"
                                        className={styles.paginationArrow}
                                        onClick={() => goToPage(currentPage - 1)}
                                        disabled={currentPage === 0}
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                    </button>
                                    {getPageNumbers(currentPage + 1, totalPages).map((page, index) => (
                                        page === '...' ? (
                                            <span key={`dots-${index}`} className={styles.paginationDots}>...</span>
                                        ) : (
                                            <button
                                                key={page}
                                                type="button"
                                                className={`${styles.paginationNumber} ${page === currentPage + 1 ? styles.paginationNumberActive : ''}`}
                                                onClick={() => goToPage(page - 1)}
                                            >
                                                {page}
                                            </button>
                                        )
                                    ))}
                                    <button
                                        type="button"
                                        className={styles.paginationArrow}
                                        onClick={() => goToPage(currentPage + 1)}
                                        disabled={currentPage + 1 >= totalPages}
                                    >
                                        <i className="fas fa-chevron-right"></i>
                                    </button>
                                </div>
                                <div className={styles.pageSizeGroup}>
                                    <label className={styles.sortLabel}>{t('labelPrikazi')}</label>
                                    <select
                                        className="form-control form-control-sm"
                                        value={pageSize}
                                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                    >
                                        <option value={3}>3 {t('poStranici')}</option>
                                        <option value={6}>6 {t('poStranici')}</option>
                                        <option value={12}>12 {t('poStranici')}</option>
                                        <option value={24}>24 {t('poStranici')}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ArticleDocumentationPage;
